import { Spinner } from '@consigli/facade';
import {
  useGetBuildingPackageQuery,
  useBuildingPackageId,
  useProject,
  useProjectId,
} from '@consigli/hooks';
import { JobType } from '@consigli/types';

import { useRegisterBreadcrumb } from '@/components';
import { PageHeader } from '@/components/page-header';
import { Tabs } from '@/components/tabs';
import { ServiceType } from '@/utils/types';
import { useTabParam } from '@/utils/use-tab-param';

import { DocumentsTab } from './documents/documents-tab';
import { UploadBuildingPackageDocumentForm } from './documents/form/upload-building-package-document-form';
import { DownloadBuildingJsonButton } from './download-building-json-button';
import { CreateFindingForm } from './findings/create-finding-form/create-finding-form';
import { PipelinesTab } from './pipelines/pipelines-tab';
import { StartPipelineMenu } from './pipelines/start-pipeline-menu';

const TAB_VALUES = ['documents', 'findings', 'pipelines'] as const;
type TabValue = (typeof TAB_VALUES)[number];

export const BuildingPackagePage = () => {
  const buildingPackageId = useBuildingPackageId();
  const projectId = useProjectId();
  const [selectedTab, setSelectedTab] = useTabParam<TabValue>('documents', TAB_VALUES);
  const { project } = useProject(projectId);
  const { data, isLoading } = useGetBuildingPackageQuery({
    projectId,
    buildingPackageId,
  });

  useRegisterBreadcrumb(projectId, project?.name);
  useRegisterBreadcrumb(buildingPackageId, data?.name);

  if (isLoading) return <Spinner />;

  if (!data) return <div>Building package not found</div>;

  return (
    <div>
      <PageHeader title="Building Package" subtitle={data.name}>
        <div className="flex gap-2">
          <DownloadBuildingJsonButton />
          {selectedTab === 'documents' && <UploadBuildingPackageDocumentForm />}
          {selectedTab === 'findings' && (
            <CreateFindingForm projectId={projectId} packageId={buildingPackageId} />
          )}
          {selectedTab === 'pipelines' && (
            <StartPipelineMenu
              projectId={projectId}
              packageId={buildingPackageId}
              jobType={JobType.DOCUMENT_PACKAGE}
            />
          )}
        </div>
      </PageHeader>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value as TabValue)}
        tabs={[
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <DocumentsTab
                serviceId={buildingPackageId}
                projectId={projectId}
                serviceType={ServiceType.BUILDING_PACKAGE}
              />
            ),
          },
          // {
          //   value: 'findings',
          //   label: 'Findings',
          //   content: <FindingsTab documentPackage={data} />,
          // },
          {
            value: 'pipelines',
            label: 'Pipelines',
            content: <PipelinesTab projectId={projectId} packageId={buildingPackageId} />,
          },
        ]}
      />
    </div>
  );
};
